import React, { lazy } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import NotFound from "../Pages/NotFound/NotFound";
import Store from "../index";
import { normalizeUserRole } from "../utilities/userRoleUtils";

// Lazy load all pages
const ProductList = lazy(() => import("../Pages/Products/productList"));
const GRN = lazy(() => import("../Pages/GRN/GRN"));
const Customers = lazy(() => import("../components/Customers/Customers"));
const PosContainer = lazy(() => import("../containers/PosContainer/PosContainer"));
const Inventory = lazy(() => import("../Pages/Inventory/Inventory"));
const EditOrder = lazy(() => import("../components/Order/EditOrder"));
const ModernOrders = lazy(() => import("../Pages/CreateOrders/ModernOrders"));
const SalesJourney = lazy(() => import("../Pages/SalesJourney/SalesJourney"));
const QuotationList = lazy(() => import("../Pages/Quotations/QuotationList"));
const QuotationForm = lazy(() => import("../Pages/Quotations/QuotationForm"));
const QuotationDetail = lazy(() => import("../Pages/Quotations/QuotationDetail"));
const DeliveryList = lazy(() => import("../Pages/Delivery/DeliveryList"));
const DeliveryForm = lazy(() => import("../Pages/Delivery/DeliveryForm"));
const DeliveryDetail = lazy(() => import("../Pages/Delivery/DeliveryDetail"));
const InvoiceList = lazy(() => import("../Pages/Invoice/InvoiceList"));
const InvoiceForm = lazy(() => import("../Pages/Invoice/InvoiceForm"));
const InvoiceDetail = lazy(() => import("../Pages/Invoice/InvoiceDetail"));
const SalesPipeline = lazy(() => import("../Pages/Orders/SalesPipeline"));
const ViewOrders = lazy(() => import("../Pages/Orders/ViewOrders"));
const Refund = lazy(() => import("../components/Order/Refund"));
const ViewOrder = lazy(() => import("../Pages/OrderDetail/ViewOrder"));
const AddCustomerForm = lazy(() => import("../components/Customers/AddCustomerForm"));
const AddProductForm = lazy(() => import("../components/Products/AddProductForm"));
const EditProduct = lazy(() => import("../components/Products/EditProduct"));
const EditCustomer = lazy(() => import("../components/Customers/Editcustomer"));
const InventoryHistory = lazy(() => import("../components/InventoryHistory"));
const Checkout = lazy(() => import("../components/stripe/checkout"));
const CancelOrder = lazy(() => import("../components/Order/CancelOrder"));
const PurchaseOrders = lazy(() => import("../Pages/PurchaseOrders/PurchaseOrders"));
const Vendors = lazy(() => import("../Pages/Vendors/Vendors"));
const AddVendor = lazy(() => import("../Pages/Vendors/AddVendor"));
const ViewPurchaseOrder = lazy(() => import("../Pages/ViewPurchaseOrder/ViewPurchaseOrder"));
const CategoryList = lazy(() => import("../Pages/Categories/CategoryList"));
const CategoryForm = lazy(() => import("../Pages/Categories/CategoryForm"));
const OverviewDashboard = lazy(() => import("../Pages/Reports/Dashboard/OverviewDashboard"));
const SalesOrderDashboard = lazy(() => import("../Pages/Reports/Dashboard/SalesOrderDashboard"));
const SalesOverview = lazy(() => import("../Pages/Reports/Sales/SalesOverview"));
const SalesByProduct = lazy(() => import("../Pages/Reports/Sales/SalesByProduct"));
const InventoryOverview = lazy(() => import("../Pages/Reports/Inventory/InventoryOverview"));
const CustomerAnalytics = lazy(() => import("../Pages/Reports/Customers/CustomerAnalytics"));
const ProfitLoss = lazy(() => import("../Pages/Reports/Financial/ProfitLoss"));
const TopCustomersBySales = lazy(() => import("../Pages/Reports/Sales/TopCustomersBySales"));
const PurchaseOverview = lazy(() => import("../Pages/Reports/Purchases/PurchaseOverview"));
const ProfitByProduct = lazy(() => import("../Pages/Reports/Financial/ProfitByProduct"));
const DataWizard = lazy(() => import("../Pages/Configuration/DataWizard"));
const BusinessSettings = lazy(() => import("../Pages/Configuration/BusinessSettings"));
const ZatcaSettings = lazy(() => import("../Pages/Configuration/ZatcaSettings"));
const ZatcaInvoices = lazy(() => import("../Pages/Zatca/ZatcaInvoices"));
const PrinterSettings = lazy(() => import("../Pages/Configuration/PrinterSettings"));

function AppRoutes({ authenticated, userRole, appType }) {
    const normalizedRole = normalizeUserRole(userRole);

    // User role checks with normalized role
    const isAdmin = normalizedRole === "Admin" || normalizedRole === "ADMIN";
    const isManager = normalizedRole === "Manager" || normalizedRole === "MANAGER";
    const isAgent = normalizedRole === "Agent" || normalizedRole === "AGENT";
    
    // Normalize appType (remove trailing dash)
    const cleanAppType = appType ? appType.replace(/-$/, '') : appType;
    
    // Determine if the business type is LOGISTIC
    const isLogistic = Store.getState()?.globalState?.bussinessType === "LOGISTIC";

    return (
        <>
            {/* Manager Routes */}
            {authenticated && isManager && !isAdmin && (
                <Switch>
                    <Route path="/" exact render={(props) => <ProductList {...props} />} />
                    <Route path="/checkout" render={(props) => <Checkout {...props} />} />
                    <Route path="/Customers" render={(props) => <Customers {...props} />} />
                    <Route path="/editcustomer/:id" render={(props) => <EditCustomer {...props} />} />
                    <Route path="/customer" render={(props) => <AddCustomerForm {...props} />} />
                    <Route path="/Products" render={(props) => <ProductList {...props} />} />
                    <Route path="/ProductForm" render={(props) => <AddProductForm {...props} />} />
                    <Route path="/editproduct/:id" render={(props) => <EditProduct {...props} />} />
                    <Route path="/Categories" render={(props) => <CategoryList {...props} />} />
                    <Route path="/Category/Add" render={(props) => <CategoryForm {...props} />} />
                    <Route path="/inventory" render={(props) => <Inventory {...props} />} />
                    <Route path="/inventryHistory" render={(props) => <InventoryHistory {...props} />} />
                    <Route path="/SalesJourney" render={(props) => <SalesJourney {...props} />} />
                    <Route path="/Sales Order" render={(props) => <ViewOrders {...props} />} />
                    <Route path="/ViewOrders" render={(props) => <ViewOrders {...props} />} />
                    <Route path="/ViewOrder/:id" exact render={(props) => <ViewOrder {...props} />} />
                    <Route path="/ViewOrder" render={(props) => <ViewOrder {...props} />} />
                    <Route path="/CreateOrders" render={(props) => <ModernOrders {...props} />} />
                    <Route path="*" render={() => <NotFound />} />
                </Switch>
            )}

            {/* Agent Routes */}
            {authenticated && isAgent && !isAdmin && !isManager && (
                <Switch>
                    <Route path="/POS" exact render={(props) => <PosContainer {...props} />} />
                    <Route path="/checkout" render={(props) => <Checkout {...props} />} />
                    <Redirect to="/POS" />
                    <Route path="*" render={() => <NotFound />} />
                </Switch>
            )}

            {/* Admin routes for HPOS */}
            {authenticated && isAdmin && cleanAppType === "HPOS" && (
                <Switch>
                    <Redirect exact from="/" to="/Pos" />
                    <Route path="/configuration/data-wizard" render={(props) => <DataWizard {...props} />} />
                    <Redirect from="/configuration/zatca-settings" to="/configuration/tax-configuration" />
                    <Route path="/configuration/tax-configuration" render={(props) => <ZatcaSettings {...props} />} />
                    <Route path="/zatca/invoices" render={(props) => <ZatcaInvoices {...props} />} />
                    <Route path="/Pos" render={(props) => <PosContainer {...props} />} />
                    <Route path="/checkout" render={(props) => <Checkout {...props} />} />
                    <Route path="/Customers" render={(props) => <Customers {...props} />} />
                    <Route path="/customer" render={(props) => <AddCustomerForm {...props} />} />
                    <Route path="/editcustomer/:id" render={(props) => <EditCustomer {...props} />} />
                    <Route path="/Products" render={(props) => <ProductList {...props} />} />
                    <Route path="/ProductForm" render={(props) => <AddProductForm {...props} />} />
                    <Route path="/editproduct/:id" render={(props) => <EditProduct {...props} />} />
                    <Route path="/Categories" render={(props) => <CategoryList {...props} />} />
                    <Route path="/Category/Add" render={(props) => <CategoryForm {...props} />} />
                    <Route path="/inventory" render={(props) => <Inventory {...props} />} />
                    <Route path="/inventryHistory" render={(props) => <InventoryHistory {...props} />} />
                    <Route path="*" render={() => <NotFound />} />
                </Switch>
            )}

            {/* 🚚 Admin routes for HOMS-LOGISTIC */}
            {authenticated && isAdmin && cleanAppType === "HOMS" && isLogistic && (
                <Switch>
                    <Redirect exact from="/" to="/reports/dashboard" />
                    <Route path="/configuration/data-wizard" render={(props) => <DataWizard {...props} />} />
                    <Route path="/configuration/business-settings" render={(props) => <BusinessSettings {...props} />} />
                    <Redirect from="/configuration/zatca-settings" to="/configuration/tax-configuration" />
                    <Route path="/configuration/tax-configuration" render={(props) => <ZatcaSettings {...props} />} />
                    <Route path="/zatca/invoices" render={(props) => <ZatcaInvoices {...props} />} />
                    <Route path="/configuration/printer-settings" render={(props) => <PrinterSettings {...props} />} />
                    <Route path="/reports/dashboard" render={(props) => <SalesOrderDashboard {...props} />} />
                    <Route path="/reports/sales/overview" render={(props) => <SalesOverview {...props} />} />
                    <Route path="/reports/sales/by-product" render={(props) => <SalesByProduct {...props} />} />
                    <Route path="/reports/sales/top-customers" render={(props) => <TopCustomersBySales {...props} />} />
                    <Route path="/Vendors" render={(props) => <Vendors />} />
                    <Route path="/addVendor" render={(props) => <AddVendor {...props} />} />
                    <Route path="/Purchase Orders" render={(props) => <ViewPurchaseOrder {...props} />} />
                    <Route path="/ViewPurchaseOrders" render={(props) => <PurchaseOrders {...props} />} />
                    <Route path="/CreateOrders" render={(props) => <ModernOrders {...props} />} />
                    <Route path="/SalesJourney" render={(props) => <SalesJourney {...props} />} />
                    <Route path="/Sales Order" render={(props) => <ViewOrders {...props} />} />
                    <Route path="/ViewOrders" render={(props) => <ViewOrders {...props} />} />
                    <Route path="/edit/:orderId" render={(props) => <EditOrder {...props} />} />
                    <Route path="/order/cancel/:id" render={(props) => <CancelOrder {...props} />} />
                    <Route path="/ViewOrder/:id" exact render={(props) => <ViewOrder {...props} />} />
                    <Route path="/ViewOrder" render={(props) => <ViewOrder {...props} />} />
                    <Route path="/Customers" render={(props) => <Customers {...props} />} />
                    <Route path="/Products" render={(props) => <ProductList {...props} />} />
                    <Route path="/Categories" render={(props) => <CategoryList {...props} />} />
                    <Route path="/Category/Add" render={(props) => <CategoryForm {...props} />} />
                    <Route path="/inventory" render={(props) => <Inventory {...props} />} />
                    <Route path="/grn" render={(props) => <GRN {...props} />} />
                    <Route path="/ProductForm" render={(props) => <AddProductForm {...props} />} />
                    <Route path="/editcustomer/:id" render={(props) => <EditCustomer {...props} />} />
                    <Route path="/customer" render={(props) => <AddCustomerForm {...props} />} />
                    <Route path="/refund" render={(props) => <Refund {...props} />} />
                    <Route path="/editproduct/:id" render={(props) => <EditProduct {...props} />} />
                    <Route path="/inventryHistory" render={(props) => <InventoryHistory {...props} />} />
                    <Route path="*" render={() => <NotFound />} />
                </Switch>
            )}

            {/* 📦 Admin routes for HOMS (Standard - NOT Logistic) */}
            {authenticated && isAdmin && cleanAppType === "HOMS" && !isLogistic && (
                <Switch>
                  <Redirect exact from="/" to="/reports/dashboard" />
                    <Route path="/configuration/data-wizard" render={(props) => <DataWizard {...props} />} />
                    <Route path="/configuration/business-settings" render={(props) => <BusinessSettings {...props} />} />
                    <Redirect from="/configuration/zatca-settings" to="/configuration/tax-configuration" />
                    <Route path="/configuration/tax-configuration" render={(props) => <ZatcaSettings {...props} />} />
                    <Route path="/zatca/invoices" render={(props) => <ZatcaInvoices {...props} />} />
                    <Route path="/configuration/printer-settings" render={(props) => <PrinterSettings {...props} />} />
                    <Route path="/reports/dashboard" render={(props) => <SalesOrderDashboard {...props} />} />
                    <Route path="/reports/sales/overview" render={(props) => <SalesOverview {...props} />} />
                    <Route path="/reports/sales/by-product" render={(props) => <SalesByProduct {...props} />} />
                    <Route path="/reports/sales/top-customers" render={(props) => <TopCustomersBySales {...props} />} />
                    <Route path="/SalesPipeline" render={(props) => <SalesPipeline {...props} />} />
                    <Route path="/Quotations" render={(props) => <QuotationList {...props} />} />
                    <Route path="/Quotation/New" render={(props) => <QuotationForm {...props} />} />
                    <Route path="/Quotation/Edit/:id" render={(props) => <QuotationForm {...props} />} />
                    <Route path="/Quotation/:id" exact render={(props) => <QuotationDetail {...props} />} />
                    <Route path="/Deliveries" render={(props) => <DeliveryList {...props} />} />
                    <Route path="/Delivery/New" render={(props) => <DeliveryForm {...props} />} />
                    <Route path="/Delivery/:id" exact render={(props) => <DeliveryDetail {...props} />} />
                    <Route path="/Invoices" render={(props) => <InvoiceList {...props} />} />
                    <Route path="/Invoice/New" render={(props) => <InvoiceForm {...props} />} />
                    <Route path="/Invoice/:id" exact render={(props) => <InvoiceDetail {...props} />} />
                    <Route path="/Vendors" render={(props) => <Vendors />} />
                    <Route path="/addVendor" render={(props) => <AddVendor {...props} />} />
                    <Route path="/Purchase Orders" render={(props) => <ViewPurchaseOrder {...props} />} />
                    <Route path="/ViewPurchaseOrders" render={(props) => <PurchaseOrders {...props} />} />
                    <Route path="/CreateOrders" render={(props) => <ModernOrders {...props} />} />
                    <Route path="/SalesJourney" render={(props) => <SalesJourney {...props} />} />
                    <Route path="/Sales Order" render={(props) => <ViewOrders {...props} />} />
                    <Route path="/ViewOrders" render={(props) => <ViewOrders {...props} />} />
                    <Route path="/edit/:orderId" render={(props) => <EditOrder {...props} />} />
                    <Route path="/order/cancel/:id" render={(props) => <CancelOrder {...props} />} />
                    <Route path="/ViewOrder/:id" exact render={(props) => <ViewOrder {...props} />} />
                    <Route path="/ViewOrder" render={(props) => <ViewOrder {...props} />} />
                    <Route path="/Customers" render={(props) => <Customers {...props} />} />
                    <Route path="/Products" render={(props) => <ProductList {...props} />} />
                    <Route path="/Categories" render={(props) => <CategoryList {...props} />} />
                    <Route path="/Category/Add" render={(props) => <CategoryForm {...props} />} />
                    <Route path="/inventory" render={(props) => <Inventory {...props} />} />
                    <Route path="/grn" render={(props) => <GRN {...props} />} />
                    <Route path="/ProductForm" render={(props) => <AddProductForm {...props} />} />
                    <Route path="/editcustomer/:id" render={(props) => <EditCustomer {...props} />} />
                    <Route path="/customer" render={(props) => <AddCustomerForm {...props} />} />
                    <Route path="/refund" render={(props) => <Refund {...props} />} />
                    <Route path="/editproduct/:id" render={(props) => <EditProduct {...props} />} />
                    <Route path="/inventryHistory" render={(props) => <InventoryHistory {...props} />} />
                    <Route path="*" render={() => <NotFound />} />
                </Switch>
            )}

            {/* Admin routes for HCRM */}
            {authenticated && isAdmin && cleanAppType === "HCRM" && (
                <Switch>
                    <Redirect exact from="/" to="/Customers" />
                    <Route path="/Customers" render={(props) => <Customers {...props} />} />
                    <Route path="/configuration/data-wizard" render={(props) => <DataWizard {...props} />} />
                    <Route path="/editcustomer/:id" render={(props) => <EditCustomer {...props} />} />
                    <Route path="/customer" render={(props) => <AddCustomerForm {...props} />} />
                    <Route path="*" render={() => <NotFound />} />
                </Switch>
            )}

            {/* Admin routes for HPRMS */}
            {authenticated && isAdmin && cleanAppType === "HPRMS" && (
                <Switch>
                    <Redirect exact from="/" to="/reports/dashboard" />
                    <Route path="/reports/dashboard" render={(props) => <OverviewDashboard appType={appType} {...props} />} />
                    <Route path="/configuration/data-wizard" render={(props) => <DataWizard {...props} />} />
                    <Route path="/reports/purchases/overview" render={(props) => <PurchaseOverview {...props} />} />
                    <Route path="/reports/inventory/overview" render={(props) => <InventoryOverview {...props} />} />
                    <Route path="/Vendors" render={(props) => <Vendors />} />
                    <Route path="/addVendor" render={(props) => <AddVendor {...props} />} />
                    <Route path="/Purchase Orders" render={(props) => <ViewPurchaseOrder {...props} />} />
                    <Route path="/ViewPurchaseOrders" render={(props) => <PurchaseOrders {...props} />} />
                    <Route path="/Products" render={(props) => <ProductList {...props} />} />
                    <Route path="/ProductForm" render={(props) => <AddProductForm {...props} />} />
                    <Route path="/editproduct/:id" render={(props) => <EditProduct {...props} />} />
                    <Route path="/Categories" render={(props) => <CategoryList {...props} />} />
                    <Route path="/Category/Add" render={(props) => <CategoryForm {...props} />} />
                    <Route path="/inventory" render={(props) => <Inventory {...props} />} />
                    <Route path="/grn" render={(props) => <GRN {...props} />} />
                    <Route path="/inventryHistory" render={(props) => <InventoryHistory {...props} />} />
                    <Route path="*" render={() => <NotFound />} />
                </Switch>
            )}

            {/* Admin routes for HPiMS */}
            {authenticated && isAdmin && cleanAppType === "HPiMS" && (
                <Switch>
                    <Redirect exact from="/" to="/inventory" />
                    <Route path="/Products" render={(props) => <ProductList {...props} />} />
                    <Route path="/configuration/data-wizard" render={(props) => <DataWizard {...props} />} />
                    <Route path="/ProductForm" render={(props) => <AddProductForm {...props} />} />
                    <Route path="/editproduct/:id" render={(props) => <EditProduct {...props} />} />
                    <Route path="/Categories" render={(props) => <CategoryList {...props} />} />
                    <Route path="/Category/Add" render={(props) => <CategoryForm {...props} />} />
                    <Route path="/inventory" render={(props) => <Inventory {...props} />} />
                    <Route path="*" render={() => <NotFound />} />
                </Switch>
            )}

            {/* Admin routes for HRMS */}
            {authenticated && isAdmin && cleanAppType === "HRMS" && (
                <Switch>
                    <Redirect exact from="/" to="/reports/dashboard" />
                    <Route path="/reports/dashboard" render={(props) => <OverviewDashboard appType={appType} {...props} />} />
                    <Route path="/reports/sales/overview" render={(props) => <SalesOverview {...props} />} />
                    <Route path="/reports/sales/by-product" render={(props) => <SalesByProduct {...props} />} />
                    <Route path="/reports/sales/top-customers" render={(props) => <TopCustomersBySales {...props} />} />
                    <Route path="/reports/inventory/overview" render={(props) => <InventoryOverview {...props} />} />
                    <Route path="/reports/customers/analytics" render={(props) => <CustomerAnalytics {...props} />} />
                    <Route path="/reports/purchases/overview" render={(props) => <PurchaseOverview {...props} />} />
                    <Route path="/reports/financial/profit-loss" render={(props) => <ProfitLoss {...props} />} />
                    <Route path="/reports/financial/profit-by-product" render={(props) => <ProfitByProduct {...props} />} />
                    <Route path="*" render={() => <NotFound />} />
                </Switch>
            )}

            {/* Admin routes for HVMS */}
            {authenticated && isAdmin && cleanAppType === "HVMS" && (
                <Switch>
                    <Redirect exact from="/" to="/Vendors" />
                    <Route path="/Vendors" render={(props) => <Vendors />} />
                    <Route path="/configuration/data-wizard" render={(props) => <DataWizard {...props} />} />
                    <Route path="/addVendor" render={(props) => <AddVendor {...props} />} />
                    <Route path="*" render={() => <NotFound />} />
                </Switch>
            )}
        </>
    );
}

export default AppRoutes;